import React, { useCallback, useState } from 'react'
import { AppSvg, ButtonVertical, FormattedHTMLMessage, newIcons, Spinner } from '@web/walter-ui-kit'
import { GetCard } from '@web/walter-lib/api'
import { lang } from './lang'
import DetailsModal from './DetailsModal'
import { createAES, encryptKeyByRSA, importVerifyRsaKey, decryptAES, verifySignatureRsa, trimNonJson } from './crypto'
import { Details as DetailsState } from './types'
import ConfirmationModal from '~components/ConfirmationModal'
import { cardsApi, useGetSensitiveData } from '~api'
import { usePromise } from '~hooks/usePromise'
import { processPermissionError } from '~utils/processPermissionError'

interface Props {
    isDisabled?: boolean
    card?: GetCard
}

function Details({ isDisabled, card }: Props) {
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        showTwoFa: false,
        showVerificationCode: false,
    })

    const [details, setDetails] = useState<DetailsState & { isOpen: boolean }>()

    const { data, isLoading } = useGetSensitiveData({ id: card?.id || '' }, { shouldRequest: !!card?.id })

    const onClose = useCallback(() => {
        setDetails(s => ({ ...s, isOpen: false }))
    }, [])

    const { send: getDetails, isLoading: isGetDetailsLoading } = usePromise(async () => {
        if (data?.type === 'encrypted') {
            const aesKey = await createAES()
            const importedSignatureKey = await importVerifyRsaKey(data?.signature_public_key)

            const encryptedKey = await encryptKeyByRSA(aesKey, data?.encryption_public_key)

            let sensDetails
            try {
                sensDetails = await cardsApi.postSensitiveDetails({
                    id: card?.id || '',
                    key: encryptedKey,
                })
            } catch (error) {
                const { showVerificationCode, showTwoFa } = processPermissionError(error)

                if (showVerificationCode || showTwoFa) {
                    setConfirmModal({ isOpen: true, showTwoFa, showVerificationCode })
                    return
                }

                throw error
            }

            const isValid = await verifySignatureRsa(
                sensDetails?.encrypted_payload,
                sensDetails?.iv,
                sensDetails?.signature,
                importedSignatureKey,
                data?.signature_public_key
            )

            if (!isValid) {
                throw Error('Signature unverified')
            }

            const details = decryptAES(sensDetails?.encrypted_payload, sensDetails?.iv, aesKey)

            const parsedDetails = JSON.parse(trimNonJson(details))

            setDetails({
                code: parsedDetails?.Cvv,
                date: sensDetails?.additional_fields?.expiry_date,
                name: sensDetails?.additional_fields?.cardholder_name,
                numbers: parsedDetails?.Pan,
                isOpen: true,
            })
        }
    })

    const onSuccessConfirm = useCallback(() => {
        setConfirmModal({ isOpen: false, showTwoFa: false, showVerificationCode: false })
        void getDetails()
    }, [getDetails])

    return (
        <>
            <ConfirmationModal
                isOpen={confirmModal?.isOpen || false}
                showTwoFa={confirmModal?.showTwoFa || false}
                showVerificationCode={confirmModal?.showVerificationCode || false}
                onClose={() => setConfirmModal({ isOpen: false, showTwoFa: false, showVerificationCode: false })}
                onSuccess={onSuccessConfirm}
                resource={cardsApi.resources.sensitiveDetails(card?.id || '')}
                size="xs"
            />
            <DetailsModal isOpen={!!details?.isOpen} onClose={onClose} details={details} />
            <ButtonVertical
                dataPointer="details"
                label={<FormattedHTMLMessage {...lang.details} />}
                role="button"
                icon={
                    isGetDetailsLoading ? (
                        <Spinner size={24} />
                    ) : (
                        <AppSvg svgName={newIcons.ic24Card} path="new-icons" size={24} />
                    )
                }
                onClick={getDetails}
                isDisabled={isDisabled || isLoading || data?.type !== 'encrypted'}
            />
        </>
    )
}

export default Details
